import { FC, useEffect, useState } from 'react';

import { parseDate } from '@datepicker-react/hooks';
import moment from 'moment';

import { callService } from '@api/caller';
import { WebUrlType, HotelSearchViewModel } from '@api/index';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { HOST_URL, HOTEL_PAST_SEARCH } from '@constants/constants';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { useCustomMobileDesktopBreakpoint } from '@hooks/hooks';
import clean from '@utils/cleanObject';
import {
  DateType,
  formatMomentDate,
  FORM_DATE_FORMAT,
  getCurrentDate,
  isBeforeDateTime,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import { addHotelSearchBarItemClickGTM } from '@utils/gtm/hotel-gtm';
import { LANDING_BREAKPOINT_VALUE } from '@utils/theme/breakPoint';

import BookerGuestListingContainer from './common/BookerGuestListingContainer';
import {
  BookerSearchDrawer,
  BookerSearchViewModel,
} from './common/BookerSearchDrawer';
import {
  generateSearchRooms,
  hotelItemsTOStringify,
} from './common/bookerUtils';
import { BookerHr } from './common/styled';
import HotelBookerDatePicker from './HotelBookerDatePicker';

const placeholder = 'Otel Adı Veya Konum';

export interface HotelBookerError {
  location?: boolean;
}

interface HotelTabProps {
  onHrefUpdate: (href: string) => void;
  onSearchCalled: () => void;
  href: string;
  type: string;
  setError: React.Dispatch<
    React.SetStateAction<Partial<HotelBookerError | undefined>>
  >;
  setInitialError: React.Dispatch<
    React.SetStateAction<Partial<HotelBookerError | undefined>>
  >;
  error?: HotelBookerError;
  initialError?: HotelBookerError;
}

const isDateOver = (date: Date) => {
  return isBeforeDateTime(
    moment(date),
    moment(
      parseDate(
        getCurrentDate().format(FORM_DATE_FORMAT),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );
};

export const HotelTabContent: FC<HotelTabProps> = ({
  onHrefUpdate,
  onSearchCalled,
  href,
  type,
  error,
  setError,
  setInitialError,
  initialError,
}) => {
  const [hrefProps, setHrefProps] = useState<{
    [key: string]: string | number | string[] | undefined;
  }>();
  const { xl } = useCustomMobileDesktopBreakpoint(LANDING_BREAKPOINT_VALUE);

  const { hotelRoomsList } = useSearchBarContext();
  const [hotelRooms, setHotelRooms] = useState<
    Array<ListingSearchRoomInterface>
  >([...hotelRoomsList]);

  const [location, setLocation] = useState<BookerSearchViewModel | null>(null);
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(null);
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(null);
  const [pastHotelItems, setPastHotelItems] = useState<any[]>([]);

  // ===================================================
  // Read past search items from local storage
  // Remove outdated items from the list
  // Update the local storage items
  // ===================================================
  useEffect(() => {
    const pastItems =
      typeof window !== 'undefined' && localStorage.getItem(HOTEL_PAST_SEARCH)
        ? JSON.parse(localStorage.getItem(HOTEL_PAST_SEARCH) || '')
        : [];

    const filteredItem = pastItems.filter(
      (item: any) =>
        !isDateOver(parseDate(item.in, String(`yyyy-MM-dd`), new Date()))
    );

    if (typeof window !== 'undefined')
      localStorage.setItem(
        HOTEL_PAST_SEARCH,
        JSON.stringify(filteredItem, hotelItemsTOStringify)
      );

    setPastHotelItems(filteredItem);
  }, []);

  // ===========================================
  // Filter and set href
  // ===========================================
  const handleFilterAndUpdateUrl = (
    props: {
      [key: string]: string | number | undefined;
    } = { slug: '' }
  ) => {
    const { slug, ...params } = props;

    const url = Object.entries(params)
      .map(([key, val]) => {
        if (key === 'room') return val;
        return `${key}=${val}`;
      })
      .join('&');

    return `${slug || ''}${url ? `?${url}` : ''}`;
  };

  // ===========================================
  // Handel date change
  // ===========================================
  const onDatesChange = (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => {
    const props = {
      ...hrefProps,
      in: formatMomentDate(dates?.startDate, URL_DATE_FORMAT),
      out: formatMomentDate(dates?.endDate, URL_DATE_FORMAT),
    };
    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Handel on apply
  // ===========================================
  const onApplyGuest = (hr: ListingSearchRoomInterface[]) => {
    const roomUrlStr = generateSearchRooms(hr);
    const props = { ...hrefProps, room: roomUrlStr };

    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Handel location search
  // ===========================================
  const onInputChange = (item: BookerSearchViewModel) => {
    const props = {
      ...hrefProps,
      slug: item.url,
    };

    setHrefProps(props);
    setLocation(item);
    addHotelSearchBarItemClickGTM(item);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Clear past search history items
  // ===========================================
  const clearPastHotelSearch = () => {
    if (typeof window !== 'undefined')
      localStorage.removeItem(HOTEL_PAST_SEARCH);
    setPastHotelItems([]);
  };

  // ===========================================
  // Update current rooms with past search rooms when clicking on past item
  // ===========================================
  const setPreviousHotelRooms = (item: BookerSearchViewModel) => {
    const targetItem = pastHotelItems.find(
      (i: BookerSearchViewModel) => i.text === item.text
    );

    if (!targetItem) return;

    setHotelRooms(targetItem.rooms);

    if (targetItem.in && targetItem.out) {
      setLocalStartDate(moment(targetItem.in));
      setLocalEndDate(moment(targetItem.out));
    }
    const roomUrlStr = generateSearchRooms(targetItem.rooms);

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          slug: targetItem.url,
          in: targetItem.in || null,
          out: targetItem.out || null,
          room: roomUrlStr,
        })
      )
    );
  };

  // ===========================================
  //Remove one item from local storage
  // ===========================================
  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastHotelItems?.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastHotelItems(newItems);
    setLocalStartDate(null);
    setLocalEndDate(null);
    if (typeof window !== 'undefined')
      localStorage.setItem(
        HOTEL_PAST_SEARCH,
        JSON.stringify(newItems, hotelItemsTOStringify)
      );
  };
  // ===========================================
  // Keep the href props object up to date
  // ===========================================
  useEffect(() => {
    const roomUrlStr = generateSearchRooms(hotelRooms);

    setHrefProps({
      in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
      out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
      slug: location?.url,
      room: roomUrlStr,
    });

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
          out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
          slug: location?.url,
          room: roomUrlStr,
        })
      )
    );
  }, [localStartDate, localEndDate, location, hotelRooms]);

  // ===========================================
  // Handel save items to local storage
  // ===========================================
  const saveItems = async () => {
    if (!href || !type) return;

    let pastHotelSearches = [];
    let hotelItems: HotelSearchViewModel[] = [];
    let newSearch: any = {};

    switch (type) {
      case WebUrlType.HOTEL_CITY:
      case WebUrlType.HOTEL_THEME:
      case WebUrlType.HOTEL_CHAIN:
      case WebUrlType.HOTEL_REGION:
      case WebUrlType.HOTEL:
        if (
          typeof window !== 'undefined' &&
          localStorage.getItem(HOTEL_PAST_SEARCH)
        ) {
          pastHotelSearches = JSON.parse(
            localStorage.getItem(HOTEL_PAST_SEARCH) || ''
          );
        }

        hotelItems = pastHotelSearches ? [...pastHotelSearches] : [];

        if (location) {
          const data = await callService('SearchesService', 'searchesHotel', {
            s: location.text as string,
          });

          const targetLocation = data?.find(
            (item) => item.url === location.url
          );

          newSearch = {
            ...newSearch,
            ...targetLocation,
            slug: targetLocation?.url,
          };
        }

        if (localStartDate) {
          newSearch.in = formatMomentDate(localStartDate, URL_DATE_FORMAT);
        }
        if (localEndDate) {
          newSearch.out = formatMomentDate(localEndDate, URL_DATE_FORMAT);
        }

        newSearch.rooms = hotelRooms;

        if (!hotelItems.find((i) => i.url === location?.url)) {
          hotelItems.unshift(newSearch);
        } else {
          const target = hotelItems.find((i) => i.url === location?.url);

          const targetIndex = target ? hotelItems.indexOf(target) : -1;

          hotelItems.splice(targetIndex, 1);
          hotelItems.unshift(newSearch);
        }

        if (typeof window !== 'undefined')
          localStorage.setItem(
            HOTEL_PAST_SEARCH,
            JSON.stringify(hotelItems, hotelItemsTOStringify)
          );

        window.open(
          `${HOST_URL}${STATIC_ROUTES.hotelPathPrefix}/${href}`,
          '_self'
        );

        break;

      default:
        window.open(
          `${HOST_URL}${STATIC_ROUTES.hotelPathPrefix}/${href}`,
          '_self'
        );
    }
  };

  useEffect(() => {
    if (error?.location) return;
    if (!href || !type) return;
    saveItems();
  }, [onSearchCalled]);

  // ===========================================
  // Validate hotel location and hotel date
  // ===========================================
  useEffect(() => {
    if (!href.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: true,
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: false,
      }));

      setError({
        ...initialError,
        location: false,
      });
    }
  }, [href, location]);

  return (
    <div>
      <BookerSearchDrawer
        type='hotel'
        placeholder={placeholder}
        onInputLocationChange={onInputChange}
        responsiveTitle='Otel / Konum Arama'
        fetchItems={(param: string) => {
          return callService('SearchesService', 'searchesHotel', {
            s: param || '',
          });
        }}
        history={pastHotelItems}
        onClearClick={clearPastHotelSearch}
        callBack={setPreviousHotelRooms}
        onRemoveItem={removeItemFromLocalStorage}
        error={error?.location}
      />

      {xl ? <BookerHr /> : null}

      <HotelBookerDatePicker
        dateChange={onDatesChange}
        setLocalEndDate={setLocalEndDate}
        setLocalStartDate={setLocalStartDate}
        localStartDate={localStartDate}
        localEndDate={localEndDate}
      />

      <BookerHr />

      <BookerGuestListingContainer
        hotelRooms={hotelRooms}
        setHotelRooms={setHotelRooms}
        onApply={onApplyGuest}
      />
    </div>
  );
};
export default HotelTabContent;
